import Keycloak from 'keycloak-js';
/**
 * Clears an OIDC callback left in the query string before keycloak-js initialises.
 *
 * Organization invitations are the only flow that lands here with the callback in the QUERY
 * string: Keycloak drives them from the emailed link using response_mode=query, while this app
 * runs the default 'fragment'. keycloak-js only inspects `url.hash` for the mode it is
 * configured with (keycloak.js: `responseMode === 'query'` vs `'fragment'`), so it neither
 * consumes nor cleans those parameters.
 *
 * They then poison the next request. With no `redirectUri` configured, keycloak-js falls back
 * to `location.href` - which still carries `code`, `iss` and `session_state`. Keycloak rejects
 * any redirect_uri containing those (RedirectUtils keeps a hardcoded forbidden list), so the
 * invited user saw HTTP 400 "Invalid parameter: redirect_uri" instead of the application.
 *
 * Stripping them is safe: the code cannot be exchanged anyway. keycloak-js only honours a
 * callback whose `state` it finds in its own storage, and an invitation starts in Keycloak, not
 * in this browser - so there is no stored state and no token to lose. The user continues into
 * the normal login-required flow and signs in, which is the intended behaviour for an
 * invitation: the account exists by this point.
 */
const clearStaleQueryCallback = () => {
    try {
        const url = new URL(window.location.href);
        // `code` alone is not enough to act on - only strip what is unmistakably an OIDC callback.
        if (!url.searchParams.has('code') && !url.searchParams.has('session_state')) {
            return;
        }
        ['code', 'state', 'session_state', 'iss', 'error', 'error_description'].forEach((param) => url.searchParams.delete(param));
        window.history.replaceState(window.history.state, '', url.toString());
    }
    catch (_a) {
        // A malformed URL or a blocked history API must not stop the app from starting.
    }
};
clearStaleQueryCallback();
const keycloak = new Keycloak({
    url: process.env.KEYCLOAK_HOSTNAME_URL,
    realm: 'fashionapps',
    clientId: 'app-frontend',
});
/**
 * Tokens kept from the last visit, so a refresh or a new tab does not force a new login.
 *
 * keycloak-js holds its tokens in memory only: without this, every page load starts with no
 * session and `login-required` bounces the user through Keycloak again. Passing the stored
 * tokens to init() lets it validate them (it calls updateToken/checkLoginIframe) and carry on
 * where the last page left off. A token that is expired or revoked simply fails that check and
 * the normal login flow runs, so nothing is trusted just because it was in storage.
 *
 * Reading is wrapped because storage can throw (private mode, blocked site data).
 */
const storedTokens = () => {
    var _a;
    try {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');
        // Both are required: keycloak-js ignores a token it cannot refresh.
        if (!token || !refreshToken) {
            return {};
        }
        return { token, refreshToken, idToken: (_a = localStorage.getItem('idToken')) !== null && _a !== void 0 ? _a : undefined };
    }
    catch (_b) {
        return {};
    }
};
export const initOptions = Object.assign(Object.assign({ onLoad: 'login-required', checkLoginIframe: true }, storedTokens()), { 
    // Ask Keycloak to list every organization the user belongs to in the token's `organization`
    // claim. The backend uses it to verify the X-Organization-Id header against something
    // Keycloak actually signed. `*` (rather than a specific organization) keeps switching
    // instant: one token covers them all, so no re-login is needed to change organization.
    scope: 'openid organization:*' });
export default keycloak;
